import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Download } from "lucide-react";
import FrothAiIcon from "../common/FrothAiIcon";
import "../../css/footer.css";

const columns = [
  {
    title: "Platform",
    links: [
      { label: "FrothAI", to: "/froth-ai", ai: true },
      { label: "Test Labs", to: "/test-labs" },
      { label: "AI Evaluation", to: "/ai-evaluation" },
      { label: "Integrations", to: "/integration" },
      { label: "Mobile App", to: "/mobile-app" },
    ],
  },
  {
    title: "Solutions",
    links: [
      { label: "Why TestOps", to: "/why-testops" },
      { label: "By Team", to: "/byteam" },
      { label: "By Workflow", to: "/workflow" },
    ],
  },
  {
    title: "Support",
    links: [
      { label: "User Guide", href: "https://roboticodigital.gitbook.io/https-frothtestops.com" },
      { label: "Change Log", to: "/change-log" },
      { label: "Brochure (PDF)", href: "/Froth_TestOps_Brochure.pdf", download: true },
      { label: "Self Support", to: "/login" },
      { label: "Contact Us", to: "/contact" },
    ],
  },
];

// Plays the wordmark animation the first time it scrolls into view; the sheen only runs
// when motion is allowed.
function useWordmarkReveal() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  const [motion, setMotion] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setMotion(!reduce);
    const el = ref.current;
    if (!el || reduce || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, shown, motion];
}

export default function Footer() {
  const [wordmarkRef, wordmarkShown, motion] = useWordmarkReveal();

  return (
    <footer className="ft-footer">
      <div className="ft-container">
        <div className="ft-footer__grid">
          <div className="ft-footer__intro">
            <p className="ft-footer__lead">
              Plan, test and release <span>with confidence.</span>
            </p>
            <div className="ft-footer__actions">
              <Link to="/contact" className="ft-footer__btn ft-footer__btn--primary">
                Book a demo <ArrowRight size={16} aria-hidden="true" />
              </Link>
              <a
                href="https://roboticodigital.gitbook.io/https-frothtestops.com"
                target="_blank"
                rel="noopener noreferrer"
                className="ft-footer__btn"
              >
                Read the docs
              </a>
            </div>
          </div>

          <div className="ft-footer__cols">
            {columns.map(col => (
              <div className="ft-footer__col" key={col.title}>
                <h2 className="ft-footer__title">{col.title}</h2>
                <ul className="ft-footer__links">
                  {col.links.map(l => (
                    <li key={l.label}>
                      {l.to ? (
                        <Link to={l.to}>
                          {l.ai && <FrothAiIcon size={15} className="ft-footer__ai" />}
                          <span>{l.label}</span>
                          <ArrowRight size={14} className="ft-footer__hint" aria-hidden="true" />
                        </Link>
                      ) : (
                        <a
                          href={l.href}
                          {...(l.download
                            ? { download: "Froth_TestOps_Brochure.pdf" }
                            : { target: "_blank", rel: "noopener noreferrer" })}
                        >
                          <span>{l.label}</span>
                          {l.download ? (
                            <Download size={14} className="ft-footer__mark" aria-hidden="true" />
                          ) : (
                            <ArrowUpRight size={14} className="ft-footer__mark" aria-hidden="true" />
                          )}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Oversized wordmark; SVG textLength makes it span the container exactly at any width.
          The letters stop at the divider (y=158); only the "p" descender carries on below,
          overlaying the bottom bar in the same colour. */}
      <div className="ft-container">
        <div
          ref={wordmarkRef}
          className={`ft-footer__wordmark${wordmarkShown ? " is-in" : ""}`}
          aria-hidden="true"
        >
          <svg viewBox="-12 0 1224 204" preserveAspectRatio="xMidYMin meet">
            <defs>
              <linearGradient id="ft-wordmark-fill" gradientUnits="userSpaceOnUse" x1="0" y1="10" x2="0" y2="204">
                <stop offset="0%" stopColor="#9dbcff" stopOpacity="0.32" />
                <stop offset="100%" stopColor="#9dbcff" stopOpacity="0.08" />
              </linearGradient>
              <linearGradient id="ft-wordmark-glow" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#9dbcff" />
                <stop offset="50%" stopColor="#c5b6ff" />
                <stop offset="100%" stopColor="#9dbcff" />
              </linearGradient>
              <clipPath id="ft-wordmark-above">
                <rect x="-12" y="0" width="1224" height="158" />
              </clipPath>
              <clipPath id="ft-wordmark-below">
                <rect x="-12" y="158" width="1224" height="46" />
              </clipPath>
              {/* A narrow band of light that sweeps across the letters */}
              <linearGradient id="ft-wordmark-sheen" gradientUnits="userSpaceOnUse" x1="-260" y1="0" x2="0" y2="0">
                <stop offset="0%" stopColor="#c9dbff" stopOpacity="0" />
                <stop offset="50%" stopColor="#c9dbff" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#c9dbff" stopOpacity="0" />
                {motion && (
                  <animateTransform
                    attributeName="gradientTransform"
                    type="translate"
                    values="-200 0; 1500 0; 1500 0"
                    keyTimes="0; 0.45; 1"
                    dur="7s"
                    begin="1.6s"
                    repeatCount="indefinite"
                  />
                )}
              </linearGradient>
            </defs>
            <rect className="ft-footer__wordmark-hit" x="-12" y="0" width="1224" height="158" />
            {/* The letters rise out of the divider (the clip stays put while the text moves). */}
            <g clipPath="url(#ft-wordmark-above)">
              <g className="ft-footer__wordmark-rise">
                <text x="0" y="156" textLength="1200" lengthAdjust="spacing" className="ft-footer__wordmark-base">
                  FrothTestOps
                </text>
                <text x="0" y="156" textLength="1200" lengthAdjust="spacing" className="ft-footer__wordmark-glow">
                  FrothTestOps
                </text>
                {motion && (
                  <text x="0" y="156" textLength="1200" lengthAdjust="spacing" className="ft-footer__wordmark-sheen">
                    FrothTestOps
                  </text>
                )}
              </g>
            </g>
            {/* Same text and spacing, but only the "p" is painted and only below the divider,
                so its descender lines up exactly with the letter above. It slides down last. */}
            <g clipPath="url(#ft-wordmark-below)">
              <text x="0" y="156" textLength="1200" lengthAdjust="spacing" className="ft-footer__wordmark-tail">
                FrothTestO<tspan className="ft-footer__wordmark-p">p</tspan>s
              </text>
            </g>
          </svg>
        </div>
      </div>

      <div className="ft-footer__bottom">
        <div className="ft-container ft-footer__bottom-inner">
          <p>© {new Date().getFullYear()} Copyright Robotico Digital.</p>
          <nav className="ft-footer__legal" aria-label="Legal">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/term-condition">Terms &amp; Conditions</Link>
          </nav>
          <p>
            Powered by{" "}
            <a href="https://roboticodigital.com/" target="_blank" rel="noopener noreferrer">
              Robotico Digital
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
